/* global G */
import { arrayOf, curry, isStr } from 'lib/util'

/**
 * Global Search Event Handler
 *
 * used for fetching data for rendered iterable ui components
 *
 * executes remote api call with given parameters via POST
 *
 * requires a predefined set of parameters, where as the filter body should
 * be provided via an action FILTER implementation
 *
 * @param {string} key                           listing type, ie short, verbose, etc
 * @param {Gaia.AppModule.Spec} module           app module
 * @param {Gaia.Component.Spec} component        action component
 *
 * @param {Event|object} [eventOrOptions]        information about the event that triggered this
 *                                               handler or a search term
 * @returns {Promise<{value: string, key: *}[]>} void
 */
const searchFn = async (key, module, component, eventOrOptions) => {
  try {
    const options = (eventOrOptions && eventOrOptions.detail) || eventOrOptions || {}
    const { version, api } = module[G.MODEL][G.PROPS]
    const { term: eventTerm, type: eventType, limit: eventLimit } = options
    const { filter: eventFilter, query: eventQuery } = options
    const { api: { type = eventType } = {} } = component[G.PROPS]
    const { limit, sort, unsorted = false } = component[G.PROPS]
    const { term = eventTerm, filter, transform, query } = component[G.STATE][G.META] || {}

    const filters = filter && Object.keys(filter).reduce((acc, name) => {
      const hasValues = !(filter[name]?.length === 0)
      if (hasValues) {
        acc[name] = filter[name]
      } else if (!hasValues) {
        acc.refexists = `!${name}`
      }
      return acc
    }, {})

    const params = {
      limit: (eventLimit ?? limit) || 100,
      sort: !unsorted ? sort || [['name<string>', 'asc']] : [],
      transform: transform || { key: key || 'default' },
      query: {
        term,
        type: (query?.type && arrayOf(query.type))
          || (
            query?.includes?.('metatype') || eventQuery?.includes('metatype')
              ? undefined
              : arrayOf(query?.type || [])
          ),
      },
      filter: {
        ...filters,
        ...eventFilter,
      },
    }

    const url = `/api/v${version}/search/global`

    try {
      const remoteAdapter = module[G.MODEL][G.ADAPTER][G.HTTP]
      const result = await remoteAdapter.post({ url, params })
      return result.value
    } catch {
      console.error('global search:: tried and failed getting remote resources', url)
    }
  } catch (e) {
    console.error(e)
  }
  return [
    { key: -1, value: 'Not Found' },
  ]
}

export default curry(searchFn)
